import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Users, CheckCircle2, FileText, DollarSign, Award, Cable, AlertTriangle, TrendingUp, ArrowRight } from 'lucide-react';
import { LoadingSpinner } from '../components/layout/SharedComponents';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';
import DrillDownSheet from './DataFilesAnalytics/DrillDownSheet';

const PALETTE = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

function StatCard({ icon: Icon, label, value, sub, accent = 'bg-zinc-100 text-zinc-600', onClick }) {
  const interactive = typeof onClick === 'function';
  return (
    <div
      onClick={interactive ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
      className={`rounded-xl border bg-card p-5 transition-shadow ${interactive ? 'cursor-pointer hover:shadow-md hover:border-indigo-300' : 'hover:shadow-sm'}`}
      data-testid={`supplier-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{label}</div>
          <div className="text-2xl font-bold mt-1 tabular-nums text-zinc-900">{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
        <div className={`p-2 rounded-lg ${accent}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, span = 'col-span-2', onClick }) {
  const interactive = typeof onClick === 'function';
  return (
    <div
      onClick={interactive ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={`rounded-xl border bg-card p-5 ${span} transition-shadow ${interactive ? 'cursor-pointer hover:shadow-md hover:border-indigo-300' : ''}`}
    >
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {interactive && <ArrowRight className="h-3.5 w-3.5 text-zinc-400" />}
      </div>
      <div className="h-64">{children}</div>
    </div>
  );
}

const fmtCurrency = (v) => `$${(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

export default function SuppliersDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);
  const navigate = useNavigate();
  const goSuppliers = (params = {}) => () => {
    const sp = new URLSearchParams(params).toString();
    navigate(`/data/suppliers${sp ? `?${sp}` : ''}`);
  };

  const openDrill = (queryKey, value, title) => {
    setDrill({
      title: `${title}: ${value}`,
      fetchUrl: `/api/data/suppliers?page_size=500`,
      filter: { key: queryKey, value },
      columns: [
        { key: 'supplier_code', label: 'Code' },
        { key: 'supplier_name', label: 'Name' },
        { key: 'status', label: 'Status' },
        { key: 'country', label: 'Country' },
      ],
      dataPath: 'data',
    });
  };
  const openSpendDrill = (supplierName) => {
    setDrill({
      title: `Spend Details: ${supplierName}`,
      fetchUrl: `/api/orders?page_size=500`,
      filter: { key: 'supplier_name', value: supplierName },
      columns: [
        { key: 'order_number', label: 'PO' },
        { key: 'supplier_name', label: 'Supplier' },
        { key: 'order_date', label: 'Date' },
        { key: 'order_total', label: 'Total', align: 'right', format: (v) => v != null ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—' },
      ],
      dataPath: 'data',
    });
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/api/data/suppliers-dashboard');
        setData(res.data);
      } catch (err) {
        // silent — empty state below
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="py-16"><LoadingSpinner /></div>;
  if (!data) return <div className="py-16 text-center text-muted-foreground text-sm">No data yet. Add suppliers to see insights here.</div>;

  const s = data.summary || {};

  return (
    <div className="space-y-5" data-testid="suppliers-dashboard">
      {/* Top KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Suppliers" value={s.total ?? 0} sub={`${s.active ?? 0} active · ${s.inactive ?? 0} inactive`} accent="bg-indigo-50 text-indigo-600" onClick={goSuppliers()} />
        <StatCard icon={FileText} label="Active Contracts" value={s.active_contracts ?? 0} sub={fmtCurrency(s.contract_value) + ' under contract'} accent="bg-emerald-50 text-emerald-600" onClick={() => navigate('/data/contracts')} />
        <StatCard icon={AlertTriangle} label="Expiring Soon" value={s.expiring_soon ?? 0} sub="Next 90 days" accent="bg-amber-50 text-amber-600" onClick={() => navigate('/data/contracts')} />
        <StatCard icon={Cable} label="PunchOut Enabled" value={s.punchout_enabled ?? 0} sub={`${s.iso_rated ?? 0} ISO rated · ${s.small_firm ?? 0} small firm`} accent="bg-violet-50 text-violet-600" onClick={goSuppliers({ punchout: 'true' })} />
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <ChartCard title="Suppliers by Status" subtitle="Approved · Pending · Hold · Inactive" span="md:col-span-2" onClick={goSuppliers()}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.by_status} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: '#fafafa' }} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e4e4e7' }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} style={{ cursor: 'pointer' }}>
                {(data.by_status || []).map((row, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} style={{ cursor: 'pointer' }}
                    onClick={() => openDrill('status', row.name, 'Suppliers by Status')} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="By Supplier Type" subtitle="Distribution across types" span="md:col-span-2" onClick={goSuppliers()}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data.by_type} dataKey="value" nameKey="name" cx="40%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={2} style={{ cursor: 'pointer' }}>
                {(data.by_type || []).map((row, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} style={{ cursor: 'pointer' }}
                    onClick={() => openDrill('supplier_type', row.name, 'Suppliers by Type')} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend verticalAlign="middle" align="right" layout="vertical" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <ChartCard title="New Suppliers — Last 12 Months" subtitle="Onboarding velocity" span="md:col-span-2" onClick={goSuppliers()}>
          {(data.new_per_month || []).length === 0 ? (
            <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No suppliers added in the last 12 months</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.new_per_month} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3, fill: '#10b981' }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="By Organization Size" subtitle="Spend concentration / risk indicator" span="md:col-span-2" onClick={goSuppliers()}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.by_size} layout="vertical" margin={{ top: 8, right: 16, left: 24, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} width={90} />
              <Tooltip cursor={{ fill: '#fafafa' }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]} style={{ cursor: 'pointer' }}>
                {(data.by_size || []).map((row, i) => (
                  <Cell key={i} fill={PALETTE[(i + 3) % PALETTE.length]} style={{ cursor: 'pointer' }}
                    onClick={() => openDrill('compliance.organization.size', row.name, 'Suppliers by Org Size')} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 3 — top spend (tidy list, left-aligned names, right-aligned tabular amounts) */}
      <ChartCard title="Top 10 Suppliers by Spend" subtitle="Sum of all purchase order net totals" span="" onClick={() => navigate('/orders')}>
        {(data.spend_top || []).length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No order spend recorded yet</div>
        ) : (
          <div className="overflow-y-auto h-full">
            <table className="w-full text-sm">
              <tbody>
                {data.spend_top.map((row, i) => {
                  const maxValue = Math.max(...data.spend_top.map(r => r.value || 0));
                  const pct = maxValue > 0 ? Math.round((row.value / maxValue) * 100) : 0;
                  return (
                    <tr key={row.name || i}
                        onClick={() => openSpendDrill(row.name)}
                        className="border-b border-zinc-100 last:border-0 hover:bg-indigo-50 cursor-pointer transition-colors"
                        data-testid={`supplier-spend-drill-${row.name}`}>
                      <td className="py-2 pr-3 w-6 text-xs text-zinc-400 tabular-nums text-right">{i + 1}.</td>
                      <td className="py-2 pr-3 text-left">
                        <div className="font-medium text-zinc-900 truncate">{row.name}</div>
                        <div className="mt-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: PALETTE[i % PALETTE.length] }} />
                        </div>
                      </td>
                      <td className="py-2 pl-3 text-right font-mono tabular-nums text-zinc-900 whitespace-nowrap">
                        ${(row.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </ChartCard>

      <DrillDownSheet
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        fetchUrl={drill?.fetchUrl}
        columns={drill?.columns || []}
        dataPath={drill?.dataPath}
        filter={drill?.filter}
      />
    </div>
  );
}
